import {
  type BatteryRebateInput,
  BatteryRebateInputSchema,
  type BatteryRebateOutput,
  type BatteryRebateSchedule,
  type PaybackInput,
  PaybackInputSchema,
  type PaybackOutput,
} from "./types.js";

/** Round STC count to whole certificates (STCs are issued as whole units). */
function roundStcs(n: number): number {
  return Math.round(n);
}

/**
 * Federal home-battery rebate (Cheaper Home Batteries / SRES) for one battery.
 *
 * Pure: same inputs + schedule always yield the same output. The schedule is
 * selected from the install date by the registry (`batteryScheduleForDate`) and
 * passed in — this function never reads the clock.
 *
 * Sizing:
 *   1. Eligibility gates: meets-core-eligibility, usable kWh ≥ min, nominal kWh
 *      ≤ program cap. Any failure ⇒ rebate unavailable with a reason.
 *   2. Capacity taper: usable kWh (clamped to the discounted cap) is split
 *      across the taper bands; each band's kWh × its factor × the deeming factor
 *      gives the band's STCs. The total is the battery's STC count.
 *   3. Indicative dollars: STCs × the (flagged, floating) STC price. This is an
 *      ESTIMATE — the STC units are deterministic/CER-cited, the dollar is not.
 */
export function batteryRebate(
  installDate: string,
  rawInput: BatteryRebateInput,
  schedule: BatteryRebateSchedule,
): BatteryRebateOutput {
  const input = BatteryRebateInputSchema.parse(rawInput);
  // installDate is part of the surface (it selected the schedule upstream) and
  // is echoed for auditability; the schedule is the source of truth here.
  void installDate;

  const stcPriceUsedAud = Math.min(
    input.stcPriceOverrideAud ?? schedule.indicativeStcPriceAud,
    schedule.stcClearingHouseCapAud,
  );

  const nominalForCheck = input.nominalKwh ?? input.usableKwh;

  // --- Eligibility gates ---------------------------------------------------
  let ineligibleReason: BatteryRebateOutput["ineligibleReason"] = null;
  if (!input.meetsCoreEligibility) {
    ineligibleReason = "fails-core-eligibility";
  } else if (input.usableKwh < schedule.minUsableKwh) {
    ineligibleReason = "below-min-usable";
  } else if (nominalForCheck > schedule.maxNominalKwh) {
    ineligibleReason = "over-nominal-cap";
  }

  if (ineligibleReason !== null) {
    return {
      eligibleUsableKwh: 0,
      enteredUsableKwh: input.usableKwh,
      stcCount: 0,
      taperBreakdown: [],
      indicativeRebateAud: 0,
      stcPriceUsedAud,
      rebateAvailable: false,
      ineligibleReason,
      cappedAtDiscountedCeiling: input.usableKwh > schedule.discountedUsableCapKwh,
      scheduleKey: schedule.scheduleKey,
    };
  }

  // --- Capacity taper ------------------------------------------------------
  const cappedUsable = Math.min(
    input.usableKwh,
    schedule.discountedUsableCapKwh,
  );

  const taperBreakdown = schedule.taper.map((band) => {
    const bandTop = band.toKwh ?? schedule.discountedUsableCapKwh;
    const kwhInBand = Math.max(0, Math.min(cappedUsable, bandTop) - band.fromKwh);
    const stcs = kwhInBand * band.factor * schedule.stcDeemingFactor;
    return {
      fromKwh: band.fromKwh,
      toKwh: band.toKwh,
      factor: band.factor,
      kwhInBand,
      stcs: roundStcs(stcs),
    };
  });

  // Sum unrounded STCs, then round once — avoids per-band rounding drift.
  const rawStcTotal = schedule.taper.reduce((acc, band) => {
    const bandTop = band.toKwh ?? schedule.discountedUsableCapKwh;
    const kwhInBand = Math.max(0, Math.min(cappedUsable, bandTop) - band.fromKwh);
    return acc + kwhInBand * band.factor * schedule.stcDeemingFactor;
  }, 0);
  const stcCount = roundStcs(rawStcTotal);

  const indicativeRebateAud = Math.round(stcCount * stcPriceUsedAud);

  return {
    eligibleUsableKwh: cappedUsable,
    enteredUsableKwh: input.usableKwh,
    stcCount,
    taperBreakdown,
    indicativeRebateAud,
    stcPriceUsedAud,
    rebateAvailable: stcCount > 0,
    ineligibleReason: null,
    cappedAtDiscountedCeiling: input.usableKwh > schedule.discountedUsableCapKwh,
    scheduleKey: schedule.scheduleKey,
  };
}

/** The fixed assumptions baked into a simple-payback figure. Surfaced so the
 * output reads as honest arithmetic, never a "you'll save $X" promise. */
const PAYBACK_ASSUMPTIONS: readonly string[] = [
  "Simple payback = (install cost − federal rebate) ÷ annual bill saving — it ignores battery degradation, the time value of money, finance costs, and warranty/replacement.",
  "The annual bill saving is the range YOU provided (self-consumption + avoided peak ± feed-in-tariff change); it is not a guaranteed amount and varies with your usage, tariff, and solar export.",
  "The federal rebate netted off is indicative — it tracks the floating STC price and the dated CER schedule, not a locked quote.",
  "Electricity prices and feed-in tariffs change over time; a saving estimated at today's rates will drift.",
];

/**
 * Indicative simple-payback for a home battery, returned as a low/high RANGE.
 *
 * Pure. `fastYears` uses the optimistic (HIGH) annual saving, `slowYears` the
 * pessimistic (LOW). A zero saving yields `null` (never pays back at that
 * assumption) rather than Infinity. Every assumption is surfaced — the surface
 * deliberately never returns a single "payback = X years" headline.
 */
export function paybackEstimate(rawInput: PaybackInput): PaybackOutput {
  const input = PaybackInputSchema.parse(rawInput);

  const netCostAud = Math.max(0, input.installCostAud - input.rebateAud);

  const years = (annualSaving: number): number | null =>
    annualSaving > 0 ? +(netCostAud / annualSaving).toFixed(1) : null;

  // High saving → fastest payback; low saving → slowest payback.
  const fastYears = years(input.annualSavingHighAud);
  const slowYears = years(input.annualSavingLowAud);

  const neverPaysBack = fastYears === null && slowYears === null;

  return {
    netCostAud,
    annualSavingLowAud: input.annualSavingLowAud,
    annualSavingHighAud: input.annualSavingHighAud,
    fastYears,
    slowYears,
    assumptions: PAYBACK_ASSUMPTIONS,
    neverPaysBack,
  };
}
